import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  createPayment, 
  clearFormErrors, 
  resetSubmissionStatus,
  selectPaymentCreateStatus,
  selectPaymentCreateError,
  selectPaymentFormErrors,
} from '../../features/payments/paymentSlice';
import { selectUserInformations } from '../../features/authSlice';
import SharedForm from '../../components/forms/SharedForm';
import paymentFields from '../../config/paymentFields';
import { showToast } from '../../utils/utils';

/**
 * Create Payment Page
 * Full page component for creating a new payment
 */
const CreatePaymentPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const user = useSelector(selectUserInformations);
  
  const createStatus = useSelector(selectPaymentCreateStatus);
  const createError = useSelector(selectPaymentCreateError);
  const formErrors = useSelector(selectPaymentFormErrors);

  const [formData, setFormData] = useState(
    paymentFields.reduce(
      (acc, field) => ({ ...acc, [field.name]: field.defaultValue || '' }),
      {}
    )
  );

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    
    if (formErrors[name]) {
      dispatch(clearFormErrors());
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    dispatch(createPayment({ 
      token: user.token, 
      formData 
    }));
  };

  useEffect(() => {
    if (createStatus === 'succeeded') {
      showToast('Success', 'Payment created successfully', 'success');
      dispatch(resetSubmissionStatus());
      navigate('/dashboard/payments', { replace: true });
    }
  }, [createStatus, dispatch, navigate]);

  useEffect(() => {
    if (createStatus === 'failed' && createError) {
      showToast('Error', createError, 'error');
    }
  }, [createStatus, createError]);

  useEffect(() => {
    return () => {
      dispatch(resetSubmissionStatus());
    };
  }, [dispatch]);

  const loading = createStatus === 'loading';

  return (
    <div className="min-h-screen py-4 px-4 sm:px-6 lg:px-8">
      <div className="max-w-full mx-auto">
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-6"
        >
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
                Create New Payment
              </h1>
              <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                Fill in the form below to create a new payment
              </p>
            </div>
            <button
              onClick={() => navigate('/dashboard/payments')}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Cancel
            </button>
          </div>
        </motion.div>

        {(createError || Object.values(formErrors).some(Boolean)) && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg dark:bg-red-900/20 dark:border-red-800"
          >
            {createError && (
              <p className="text-red-600 dark:text-red-400 text-sm font-medium mb-2">
                {createError}
              </p>
            )}
            {Object.entries(formErrors).map(([field, errorMsg]) => (
              errorMsg && (
                <p key={field} className="text-red-600 dark:text-red-400 text-sm">
                  {field}: {errorMsg}
                </p>
              )
            ))}
          </motion.div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4 sm:p-6 lg:p-8 relative" style={{ overflow: 'visible' }}
        >
          <SharedForm
            fields={paymentFields}
            formData={formData}
            handleChange={handleChange}
            handleSubmit={handleSubmit}
            loading={loading}
            hasCourses={true}
            hasStudents={true}
            entity="payment"
            formErrors={formErrors}
          />
        </motion.div>
      </div>
    </div>
  );
};

export default CreatePaymentPage;
